import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { trackApiError } from "../../lib/analytics/errorTracking";
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Icon,
  InfoNote,
  Input,
  Skeleton,
  Switch,
  formatKickoff,
  useToast,
} from "../../components/ui";

/**
 * The Google Sheets mirror — super admin only, and invisible to everyone else.
 *
 * Framed as "mirror" rather than "backup" throughout, on purpose. A grid can't carry pool
 * generations, settlement snapshots or the audit trail, so a competition can't be rebuilt from
 * one. Saying so here is cheaper than someone discovering it during an incident.
 */
export function SheetMirrorPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [spreadsheet, setSpreadsheet] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "sheets"],
    queryFn: () => api.admin.sheets(),
  });

  useEffect(() => {
    if (data?.spreadsheetUrl) setSpreadsheet(data.spreadsheetUrl);
  }, [data?.spreadsheetUrl]);

  const save = useMutation({
    mutationFn: (next: { enabled: boolean; spreadsheet?: string }) => api.admin.saveSheets(next),
    onSuccess: () => {
      setError(null);
      toast.show("Mirror settings saved");
      queryClient.invalidateQueries({ queryKey: ["admin", "sheets"] });
    },
    onError: (err) => {
      trackApiError(err, "save_sheet_mirror");
      setError(err instanceof ApiError ? err.message : "Couldn't save those settings.");
    },
  });

  const sync = useMutation({
    mutationFn: (force: boolean) => api.admin.syncSheets(force),
    onSuccess: (outcome) => {
      setError(null);
      toast.show(
        outcome.failed
          ? `${outcome.written} written, ${outcome.failed} failed`
          : `${outcome.written} written, ${outcome.unchanged} already current`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "sheets"] });
    },
    onError: (err) => {
      trackApiError(err, "sync_sheet_mirror");
      setError(err instanceof ApiError ? err.message : "Couldn't reach the spreadsheet.");
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) return <EmptyState title="Couldn't load mirror settings" />;

  const configured = Boolean(data.spreadsheetId);
  const ready = configured && data.credentialsPresent;

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="display-title text-2xl text-fg">Google Sheets mirror</h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-fg-subtle">
              Writes every competition into its own tab — players down, gameweeks across —
              refreshed with each results refresh. Only tabs whose contents have actually changed are
              rewritten, so an idle week costs nothing.
            </p>
          </div>
          <Badge tone={data.enabled && ready ? "alive" : "neutral"}>
            {data.enabled && ready ? "On" : data.enabled ? "Blocked" : "Off"}
          </Badge>
        </div>

        <InfoNote icon="alert">
          <strong className="font-semibold">This is a mirror, not a restorable backup.</strong> The
          grid can't carry pool generations, settlement snapshots or the audit trail, so a
          competition can't be rebuilt from it — it tells a person what happened, not the system.
          Point-in-time restore on the database is what restores. The spreadsheet also contains
          picks that haven't been revealed to players yet, so don't share it with them.
        </InfoNote>

        <div className="mt-5 space-y-4">
          <Input
            label="Spreadsheet link"
            value={spreadsheet}
            onChange={(event) => setSpreadsheet(event.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            hint="Paste the URL from the address bar, or just the id."
          />

          {!data.credentialsPresent && (
            <ErrorNote>
              No service account is configured on the API. Run{" "}
              <code className="tabular">npx wrangler secret put GOOGLE_SERVICE_ACCOUNT</code> and
              paste the whole JSON key file, then share the spreadsheet with that account's email
              as an Editor.
            </ErrorNote>
          )}

          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="flex flex-wrap items-center gap-3">
            {/* `Switch` draws only the control — its `label` is the accessible name — so the
                visible text lives beside it. */}
            <label className="flex items-center gap-3">
              <Switch
                checked={data.enabled}
                onChange={(enabled) =>
                  save.mutate({ enabled, ...(spreadsheet.trim() ? { spreadsheet } : {}) })
                }
                label="Mirror enabled"
                disabled={save.isPending || !ready}
              />
              <span className="text-sm font-medium text-fg">
                Mirror enabled
                {!ready && (
                  <span className="ml-2 font-normal text-fg-faint">
                    — add a link and the service account first
                  </span>
                )}
              </span>
            </label>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                loading={save.isPending}
                onClick={() =>
                  save.mutate({
                    enabled: data.enabled,
                    ...(spreadsheet.trim() ? { spreadsheet } : {}),
                  })
                }
              >
                Save link
              </Button>
              <Button
                loading={sync.isPending}
                disabled={!ready}
                onClick={() => sync.mutate(true)}
              >
                <Icon name="refresh" className="size-4" />
                Push now
              </Button>
            </div>
          </div>

          {data.spreadsheetUrl && (
            <a
              href={data.spreadsheetUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-text hover:underline"
            >
              Open the spreadsheet
              <Icon name="arrowRight" className="size-4" />
            </a>
          )}
        </div>
      </section>

      <section className="card p-6">
        <h3 className="mb-4 text-sm font-semibold text-fg">Per-competition status</h3>

        {!data.competitions.length ? (
          <EmptyState title="No competitions yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="pb-2 font-medium text-fg-subtle">Competition</th>
                  <th className="pb-2 font-medium text-fg-subtle">Tab</th>
                  <th className="pb-2 font-medium text-fg-subtle">Last written</th>
                  <th className="pb-2 font-medium text-fg-subtle">State</th>
                </tr>
              </thead>
              <tbody>
                {data.competitions.map((row) => (
                  <tr key={row.competitionId} className="border-b border-line/60 last:border-0">
                    <td className="py-2.5 pr-4 font-medium text-fg">{row.name}</td>
                    <td className="py-2.5 pr-4 text-fg-subtle">
                      {row.tabTitle ?? <span className="text-fg-faint">—</span>}
                    </td>
                    <td className="py-2.5 pr-4 text-fg-subtle">
                      {row.syncedAt ? formatKickoff(row.syncedAt) : (
                        <span className="text-fg-faint">Never</span>
                      )}
                    </td>
                    <td className="py-2.5">
                      {row.lastError ? (
                        <span
                          title={row.lastError}
                          className="inline-flex items-center gap-1.5 text-danger-text"
                        >
                          <Icon name="alert" className="size-3.5" />
                          Failed{row.failureCount > 1 ? ` ×${row.failureCount}` : ""}
                        </span>
                      ) : row.syncedAt ? (
                        <span className="inline-flex items-center gap-1.5 text-accent-text">
                          <Icon name="check" className="size-3.5" strokeWidth={2.4} />
                          Current
                        </span>
                      ) : (
                        <span className="text-fg-faint">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
